import { ElementType, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  Switch,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Bot, MessageSquareMore, PlugZap, Settings2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { FacebookProfile } from '../../../utils/types';
import {
  disconnectFacebookPage,
  getFacebookProfile,
  toggleFacebookAiChatResponses,
} from '../../../hooks/facebook';
import DisconnectChannelDialog from '../../../components/DisconnectChannelDialog';

export default function FacebookPageSettings() {
  const [accountData, setAccountData] = useState<FacebookProfile>();
  const { orgId, profileId } = useParams();
  const [aiEnabled, setAiEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const aiInitialized = useRef(false);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        setLoading(true);
        const response = await getFacebookProfile(orgId);
        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          setAccountData(response.data[0]);
          setAiEnabled(response.data[0].ai_chat_responses);
          aiInitialized.current = true;
        }
      } catch (error) {
        toast({
          title: 'Error fetching account',
          description: 'Unable to load this page connection.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAccount();
  }, [orgId, toast]);

  useEffect(() => {
    if (!aiInitialized.current || !accountData) return;
    toggleFacebookAiChatResponses(orgId || '', accountData.id, aiEnabled);
  }, [aiEnabled, accountData, orgId]);

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      const response = await disconnectFacebookPage(orgId, profileId);
      if (response.success) {
        toast({
          title: 'Page disconnected',
          description: 'The Facebook Page has been fully disconnected.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate('/organization/inbox/facebook-messenger');
      } else {
        throw new Error('Disconnect failed');
      }
    } catch {
      toast({
        title: 'Failed to disconnect',
        description: 'The Facebook Page could not be disconnected.',
        status: 'error',
      });
    } finally {
      setDisconnecting(false);
      onClose();
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="#f5f7fb">
        <Spinner color="#1877F2" />
      </Flex>
    );
  }

  if (!accountData) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="#f5f7fb">
        <Text color="gray.500">No Facebook Page connected.</Text>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="#f5f7fb" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <Box maxW="860px" mx="auto">
        <VStack align="stretch" spacing={6}>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="gray.500" fontWeight="700">
              Page settings
            </Text>
            <Text fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" letterSpacing="-0.04em" color="gray.900">
              Manage your Facebook connection
            </Text>
          </VStack>

          <Box bg="white" border="1px solid" borderColor="blackAlpha.100" borderRadius="28px" boxShadow="0 24px 60px rgba(15,23,32,0.06)">
            <VStack align="stretch" spacing={0}>
              <HStack spacing={4} px={6} py={6}>
                <Avatar
                  name={accountData.page_name}
                  src={accountData.metadata?.picture_url || accountData.profile_picture_url || undefined}
                  bg="#1877F2"
                  color="white"
                  size="lg"
                />
                <VStack align="flex-start" spacing={0.5}>
                  <Text fontSize="lg" fontWeight="700" color="gray.900">
                    {accountData.page_name}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {accountData.page_id}
                  </Text>
                  <Text fontSize="sm" color={accountData.is_active ? 'green.600' : 'red.500'}>
                    {accountData.is_active ? 'Connected and active' : 'Connection inactive'}
                  </Text>
                </VStack>
              </HStack>

              <Divider />

              <SettingRow
                icon={PlugZap}
                title="Connected on"
                description={new Date(accountData.connected_at).toLocaleString()}
              />

              <Divider />

              <Flex px={6} py={5} justify="space-between" align="center">
                <HStack spacing={4} align="flex-start">
                  <Icon as={Bot as ElementType} boxSize={5} color="gray.500" mt={0.5} />
                  <VStack align="flex-start" spacing={0.5}>
                    <Text fontWeight="600" color="gray.900">
                      AI chat responses
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      Allow automated replies for incoming Messenger conversations.
                    </Text>
                  </VStack>
                </HStack>
                <Switch
                  isChecked={aiEnabled}
                  onChange={(e) => setAiEnabled(e.target.checked)}
                  colorScheme="blue"
                />
              </Flex>

              <Divider />

              <HStack spacing={3} px={6} py={5}>
                <Button
                  leftIcon={<Icon as={MessageSquareMore as ElementType} boxSize={4} />}
                  variant="outline"
                  borderRadius="16px"
                  onClick={() =>
                    navigate(
                      `/organization/${orgId}/inbox/facebook/${profileId}/settings/utility-messages`,
                    )
                  }
                >
                  Utility messages
                </Button>
                <Button
                  leftIcon={<Icon as={Settings2 as ElementType} boxSize={4} />}
                  variant="outline"
                  borderRadius="16px"
                  onClick={() =>
                    navigate(`/organization/${orgId}/inbox/facebook/${profileId}/comments`)
                  }
                >
                  Comments
                </Button>
                <Button
                  colorScheme="red"
                  borderRadius="16px"
                  onClick={onOpen}
                  isLoading={disconnecting}
                >
                  Disconnect page
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Box>
      <DisconnectChannelDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDisconnect}
        title="Disconnect Facebook Page?"
        body={`This will disconnect ${accountData.page_name} from this organization and stop syncing new Messenger and comment activity.`}
        confirmLabel="Disconnect Facebook"
        isLoading={disconnecting}
      />
    </Box>
  );
}

const SettingRow = ({
  icon,
  title,
  description,
}: {
  icon: ElementType;
  title: string;
  description: string;
}) => (
  <HStack spacing={4} px={6} py={5} align="flex-start">
    <Icon as={icon} boxSize={5} color="gray.500" mt={0.5} />
    <VStack align="flex-start" spacing={0.5}>
      <Text fontWeight="600" color="gray.900">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500">
        {description}
      </Text>
    </VStack>
  </HStack>
);
